import axiosInstance from '../apiServices/axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ACTION_LOG, ACTION_LOG_COMMENTS, PROJECT_ACTION_LOG_SOURCES, PROJECT_ACTION_LOG_WORKSTREAMS } from '../apiServices/api_routes';
import { toast } from 'react-toastify';
import {
    buildProjectCsvExportFilename,
    buildProjectCsvTemplateFilename,
} from '../utils/exportPdfFilename';

const projectPath = () => localStorage['project_id'];

export const GetActionLogs = createAsyncThunk('GET_ACTION_LOGS', async () => {
    const response = await axiosInstance.get(ACTION_LOG + projectPath() + '/');
    return response;
});

export const PostActionLog = createAsyncThunk('POST_ACTION_LOG', async (body, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(ACTION_LOG + projectPath() + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PatchActionLog = createAsyncThunk('PATCH_ACTION_LOG', async ({ id, body }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(ACTION_LOG + projectPath() + '/' + id + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DeleteActionLog = createAsyncThunk('DELETE_ACTION_LOG', async (id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(ACTION_LOG + projectPath() + '/' + id + '/');
        return { id, response };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const GetProjectActionLogWorkstreams = createAsyncThunk('GET_PROJECT_ACTION_LOG_WORKSTREAMS', async () => {
    const response = await axiosInstance.get(PROJECT_ACTION_LOG_WORKSTREAMS + projectPath() + '/');
    return response;
});

export const PostProjectActionLogWorkstream = createAsyncThunk(
    'POST_PROJECT_ACTION_LOG_WORKSTREAM',
    async (body, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(PROJECT_ACTION_LOG_WORKSTREAMS + projectPath() + '/', body);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const GetProjectActionLogSources = createAsyncThunk('GET_PROJECT_ACTION_LOG_SOURCES', async () => {
    const response = await axiosInstance.get(PROJECT_ACTION_LOG_SOURCES + projectPath() + '/');
    return response;
});

export const PostProjectActionLogSource = createAsyncThunk(
    'POST_PROJECT_ACTION_LOG_SOURCE',
    async (body, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(PROJECT_ACTION_LOG_SOURCES + projectPath() + '/', body);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const ExportActionLogCSV = createAsyncThunk('EXPORT_ACTION_LOG_CSV', async (entryIds = null, { rejectWithValue, getState }) => {
    try {
        let url = ACTION_LOG + projectPath() + '/export_csv/';
        if (entryIds && entryIds.length > 0) {
            url += `?entry_ids=${entryIds.join(',')}`;
        }
        const response = await axiosInstance.get(url, { responseType: 'blob' });
        return {
            data: response.data,
            filename: buildProjectCsvExportFilename(getState, { label: 'Action Log', ids: entryIds }),
        };
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Export failed');
    }
});

export const DownloadActionLogTemplate = createAsyncThunk(
    'DOWNLOAD_ACTION_LOG_TEMPLATE',
    async (_, { rejectWithValue, getState }) => {
        try {
            const response = await axiosInstance.get(ACTION_LOG + projectPath() + '/download_template/', {
                responseType: 'blob',
            });
            return {
                data: response.data,
                filename: buildProjectCsvTemplateFilename(getState, 'Action Log'),
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message || 'Template download failed');
        }
    },
);

export const UploadActionLogCSV = createAsyncThunk(
    'UPLOAD_ACTION_LOG_CSV',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(ACTION_LOG + projectPath() + '/bulk_upload_csv/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const BulkUpdateActionLogs = createAsyncThunk(
    'BULK_UPDATE_ACTION_LOGS',
    async ({ entryIds, updates }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.patch(ACTION_LOG + projectPath() + '/bulk_update/', {
                entry_ids: entryIds,
                updates,
            });
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const BulkDeleteActionLogs = createAsyncThunk(
    'BULK_DELETE_ACTION_LOGS',
    async ({ entryIds }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(ACTION_LOG + projectPath() + '/bulk_delete/', {
                data: { entry_ids: entryIds },
            });
            return { entryIds, response };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

// Action Log comments
export const GetActionLogComments = createAsyncThunk('GET_ACTION_LOG_COMMENTS', async ({ entryId, projectId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(ACTION_LOG_COMMENTS + entryId + '/' + projectId + '/');
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PostActionLogComment = createAsyncThunk('POST_ACTION_LOG_COMMENT', async (body, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(ACTION_LOG_COMMENTS + body.entry + '/' + localStorage['project_id'] + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PatchActionLogComment = createAsyncThunk('PATCH_ACTION_LOG_COMMENT', async ({ commentId, body }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch('/toolkit/action-log-comments/' + commentId + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DeleteActionLogComment = createAsyncThunk('DELETE_ACTION_LOG_COMMENT', async ({ commentId, entryId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete('/toolkit/action-log-comments/' + commentId + '/');
        return { commentId, entryId, response };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

const initialState = {
    actionLogs: [],
    status: 'idle',
    buttonLoading: false,
    exportCSVLoading: false,
    downloadTemplateLoading: false,
    uploadCSVBtnLoading: false,
    workstreams: [],
    sources: [],
    actionLogComments: [],
    actionLogCommentBtnLoading: false,
};

const actionLogSlice = createSlice({
    name: 'actionLog',
    initialState,
    reducers: {},
    extraReducers: {
        [GetActionLogs.pending]: state => {
            state.status = 'loading';
        },
        [GetActionLogs.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            state.actionLogs = payload?.data || [];
        },
        [GetActionLogs.rejected]: () => {
            toast.error('Failed to load Action Log');
        },
        [PostActionLog.pending]: state => {
            state.buttonLoading = true;
        },
        [PostActionLog.fulfilled]: (state, { payload }) => {
            state.buttonLoading = false;
            if (payload?.data) {
                state.actionLogs = state.actionLogs ? [...state.actionLogs, payload.data] : [payload.data];
            }
            toast.success('Action added');
        },
        [PostActionLog.rejected]: (state, action) => {
            state.buttonLoading = false;
            const err = action.payload;
            if (err && typeof err === 'object') {
                Object.keys(err).forEach(key => {
                    const value = err[key];
                    if (Array.isArray(value)) {
                        value.forEach(item => toast.error(`${key}: ${item}`));
                    } else {
                        toast.error(String(value));
                    }
                });
            } else {
                toast.error('Failed to add action');
            }
        },
        [PatchActionLog.fulfilled]: (state, { payload }) => {
            if (payload?.data) {
                state.actionLogs = state.actionLogs?.map(row => (row.id === payload.data.id ? payload.data : row));
            }
        },
        [PatchActionLog.rejected]: () => {},
        [DeleteActionLog.fulfilled]: (state, { payload }) => {
            state.actionLogs = state.actionLogs?.filter(row => row.id !== payload?.id);
            toast.success('Deleted');
        },
        [DeleteActionLog.rejected]: () => {
            toast.error('Failed to delete');
        },
        [GetProjectActionLogWorkstreams.fulfilled]: (state, { payload }) => {
            state.workstreams = payload?.data || [];
        },
        [PostProjectActionLogWorkstream.fulfilled]: (state, { payload }) => {
            if (payload?.data) {
                state.workstreams = [...(state.workstreams || []), payload.data];
            }
        },
        [GetProjectActionLogSources.fulfilled]: (state, { payload }) => {
            state.sources = payload?.data || [];
        },
        [PostProjectActionLogSource.fulfilled]: (state, { payload }) => {
            if (payload?.data) {
                state.sources = [...(state.sources || []), payload.data];
            }
        },
        [GetActionLogComments.fulfilled]: (state, { payload }) => {
            state.actionLogComments = payload?.data || [];
        },
        [GetActionLogComments.rejected]: () => {
            toast.error('Failed to load comments');
        },
        [PostActionLogComment.pending]: state => {
            state.actionLogCommentBtnLoading = true;
        },
        [PostActionLogComment.fulfilled]: (state, { payload, meta }) => {
            state.actionLogCommentBtnLoading = false;
            if (payload?.data) {
                state.actionLogComments = [...(state.actionLogComments || []), payload.data];
                const entryId = payload?.data?.entry;
                if (entryId) {
                    state.actionLogs = (state.actionLogs || []).map(row =>
                        row.id === entryId ? { ...row, comments_count: (row.comments_count || 0) + 1 } : row,
                    );
                }
            }
        },
        [PostActionLogComment.rejected]: state => {
            state.actionLogCommentBtnLoading = false;
            toast.error('Failed to add comment');
        },
        [PatchActionLogComment.fulfilled]: (state, { payload }) => {
            if (payload?.data) {
                state.actionLogComments = (state.actionLogComments || []).map(c => (c.id === payload.data.id ? payload.data : c));
            }
        },
        [PatchActionLogComment.rejected]: () => {
            toast.error('Failed to update comment');
        },
        [DeleteActionLogComment.fulfilled]: (state, { payload }) => {
            state.actionLogComments = (state.actionLogComments || []).filter(c => c.id !== payload.commentId);
            state.actionLogs = (state.actionLogs || []).map(row =>
                row.id === payload.entryId ? { ...row, comments_count: Math.max(0, (row.comments_count || 0) - 1) } : row,
            );
        },
        [DeleteActionLogComment.rejected]: () => {
            toast.error('Failed to delete comment');
        },
        [ExportActionLogCSV.pending]: state => {
            state.exportCSVLoading = true;
        },
        [ExportActionLogCSV.fulfilled]: (state, action) => {
            state.exportCSVLoading = false;
            const entryIds = action.meta?.arg;
            const url = window.URL.createObjectURL(new Blob([action.payload.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', action.payload.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(
                entryIds && entryIds.length > 0
                    ? `Exported ${entryIds.length} row${entryIds.length > 1 ? 's' : ''}`
                    : 'Action Log exported',
            );
        },
        [ExportActionLogCSV.rejected]: state => {
            state.exportCSVLoading = false;
            toast.error('Export failed');
        },
        [DownloadActionLogTemplate.pending]: state => {
            state.downloadTemplateLoading = true;
        },
        [DownloadActionLogTemplate.fulfilled]: (state, action) => {
            state.downloadTemplateLoading = false;
            const url = window.URL.createObjectURL(new Blob([action.payload.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', action.payload.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Template downloaded');
        },
        [DownloadActionLogTemplate.rejected]: (state, action) => {
            state.downloadTemplateLoading = false;
            toast.error('Template download failed');
        },
        [UploadActionLogCSV.pending]: state => {
            state.uploadCSVBtnLoading = true;
        },
        [UploadActionLogCSV.fulfilled]: (state, { payload }) => {
            state.uploadCSVBtnLoading = false;
            const data = payload?.data || {};
            const items = data.items || [];
            if (items.length) {
                // Dedupe by id in case the UI already has the imported rows loaded
                const map = new Map([...(state.actionLogs || []).map(i => [i.id, i]), ...(items || []).map(i => [i.id, i])]);
                state.actionLogs = Array.from(map.values());
            }
            const created = data.created || 0;
            const skipped = data.skipped || 0;
            toast.success(`Imported ${created} action${created === 1 ? '' : 's'}`);
            if (skipped > 0) toast.warning(`Skipped ${skipped} row${skipped === 1 ? '' : 's'}`);
        },
        [UploadActionLogCSV.rejected]: state => {
            state.uploadCSVBtnLoading = false;
            toast.error('Import failed');
        },
        [BulkUpdateActionLogs.fulfilled]: (state, { payload }) => {
            const items = payload?.data?.items || [];
            if (items.length) {
                const map = new Map(items.map(i => [i.id, i]));
                state.actionLogs = (state.actionLogs || []).map(row => map.get(row.id) || row);
            }
            toast.success('Bulk update applied');
        },
        [BulkUpdateActionLogs.rejected]: () => {
            toast.error('Bulk update failed');
        },
        [BulkDeleteActionLogs.fulfilled]: (state, { payload }) => {
            const ids = new Set(payload?.entryIds || []);
            state.actionLogs = (state.actionLogs || []).filter(row => !ids.has(row.id));
            toast.success('Deleted');
        },
        [BulkDeleteActionLogs.rejected]: () => {
            toast.error('Bulk delete failed');
        },
    },
});

export default actionLogSlice.reducer;
